import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@/lib/utils';
import { successPackagesApi, successPackagesQueryKeys } from '../api';
import type { PackageFilters, CreateSuccessPackageDto, UpdateSuccessPackageDto } from '../types';

export function useSuccessPackagesList(filters?: PackageFilters) {
  return useQuery({
    queryKey: successPackagesQueryKeys.list(filters),
    queryFn: () => successPackagesApi.getAll(filters),
    staleTime: 5 * 60 * 1000,
  });
}

export function useSuccessPackage(id: number | string | undefined) {
  return useQuery({
    queryKey: successPackagesQueryKeys.detail(id ?? ''),
    queryFn: () => successPackagesApi.getById(id!),
    enabled: !!id,
    retry: 1,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSuccessPackageMutations() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (data: CreateSuccessPackageDto) => successPackagesApi.create(data),
    onSuccess: () => {
      toast.success(t('successPackageCreatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: successPackagesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorCreatingSuccessPackage')));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number | string; data: UpdateSuccessPackageDto }) =>
      successPackagesApi.update({ id, data }),
    onSuccess: (_, { id }) => {
      toast.success(t('successPackageUpdatedSuccess'));
      void queryClient.invalidateQueries({ queryKey: successPackagesQueryKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: successPackagesQueryKeys.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorUpdatingSuccessPackage')));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number | string) => successPackagesApi.delete(id),
    onSuccess: () => {
      toast.success(t('successPackageDeletedSuccess'));
      void queryClient.invalidateQueries({ queryKey: successPackagesQueryKeys.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('errorDeletingSuccessPackage')));
    },
  });

  return {
    createMutation,
    updateMutation,
    deleteMutation,
  };
}
